import { Fragment } from 'react';
import { Stack, Text } from '@chakra-ui/react';

import { NestedSelectProps } from '~/core/shared/components';
import {
  FormFieldControlledComponent,
  FormFieldControlledComponentProps,
} from '~/core/shared/types/fields';
import { ControlledSelect } from '../ControlledSelect/ControlledSelect';
import { FormLabel } from '../FormLabel.component';
import { useNestedSelect } from './hooks/useNestedSelect';
import { getNestedSelectLevelName } from './logic/getNestedSelectLevelName';

/**
 * A controlled field, so FormField hands it the path through a Controller and each level is a
 * ControlledSelect derived from that path.
 */
export const NestedSelect: FormFieldControlledComponent<
  string | null,
  NestedSelectProps
> = ({
  _ref: _refUnused,
  fieldLabel,
  hooks: _hooks,
  name,
  onBlur: _onBlurUnused,
  onChange,
  options,
  readOnly,
  value,
  ...props
}: FormFieldControlledComponentProps<string | null, NestedSelectProps>) => {
  const { labels, onSelectChange, pathLabel, segments, selects, unresolved } =
    useNestedSelect({ fieldLabel, onChange, options, value });

  return (
    <Stack gap={2}>
      {selects.map((selectOptions, index) => {
        const selectName = getNestedSelectLevelName(name, index);
        const label = labels[index];

        return (
          <Fragment key={selectName}>
            {label && (
              <FormLabel hideValidationLabel htmlFor={selectName}>
                {label}
              </FormLabel>
            )}

            <ControlledSelect
              {...props}
              disabled={readOnly}
              name={selectName}
              onChange={(next) => onSelectChange(index, next ?? '')}
              options={selectOptions}
              value={segments[index] ?? null}
            />
          </Fragment>
        );
      })}

      {unresolved && (
        <Text color="fg.error" fontSize="xs">
          {`Saved value "${pathLabel}" is no longer available. Choose a new one.`}
        </Text>
      )}
    </Stack>
  );
};

NestedSelect.controlled = true;
NestedSelect.displayName = 'NestedSelect';
